import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector } from '../redux/hooks';
import { selectUser } from '../redux/reducers/userSlice';
import { ClassDiagnosticStatusReport } from '../components/TeacherDashboard/Diagnostic/helper/chartHelper';
import {
  DiagnosticNudge,
  canHideUnfinishedDiagnostic,
  getDaysSinceDiagnosticStart,
  hasSeenNudge,
  markNudgeSeen,
  resolveCandidateNudge,
} from '../components/TeacherDashboard/Diagnostic/helper/diagnosticNudgeHelper';

interface Params {
  diagnosticResults: ClassDiagnosticStatusReport;
  /** The single class in view. Null (roadmap view, or several classes) means no nudges. */
  classId: string | null;
  isSiteAdmin: boolean;
  /** False while the diagnostic hasn't started or every student already finished. */
  enabled: boolean;
}

/**
 * Decides which time-based nudge the diagnostic widget shows, and remembers it so each
 * one appears at most once per user-class.
 */
export const useDiagnosticWidgetNudge = ({ diagnosticResults, classId, isSiteAdmin, enabled }: Params) => {
  const userId = useAppSelector(selectUser)?.id;
  const [activeNudge, setActiveNudge] = useState<DiagnosticNudge | null>(null);

  const daysSinceStart = useMemo(() => getDaysSinceDiagnosticStart(diagnosticResults), [diagnosticResults]);
  const candidateNudge = enabled && classId ? resolveCandidateNudge(daysSinceStart, isSiteAdmin) : null;

  // Decided once per (nudge, class). Reacting to `activeNudge` instead would let the
  // effect tear down the very bubble it just marked as seen.
  const decidedRef = useRef<string | null>(null);
  useEffect(() => {
    const decisionKey = candidateNudge && userId && classId ? `${candidateNudge}|${classId}` : null;
    if (decidedRef.current === decisionKey) return;
    decidedRef.current = decisionKey;

    if (!decisionKey || !candidateNudge || !userId || !classId || hasSeenNudge(candidateNudge, userId, classId)) {
      setActiveNudge(null);
      return;
    }

    markNudgeSeen(candidateNudge, userId, classId);
    setActiveNudge(candidateNudge);
  }, [candidateNudge, userId, classId]);

  return {
    activeNudge,
    dismissNudge: () => setActiveNudge(null),
    canHideWidget: enabled && !!classId && canHideUnfinishedDiagnostic(daysSinceStart),
  };
};
